import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingCropSeedingsByPrimaryDataId,
  useGetFootprintingCropTillagesByPrimaryDataId,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingCropSeedings,
  useMutateFootprintingCropTillages,
  useTranslation,
} from "utils";

import { PRACTICE_SLUG, ZERO_UUID } from "models";

import { Button, Card, Col, Flex, Form, FormInstance, Icon, IconButton, InputNumber, KlimRadioGroup, Option, PageSpin, Row, Select, Tooltip } from "components";

interface CropTillageAndSeedingFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
}

interface AppliedPractice {
  id?: string;
  practiceId: string;
  implementedOnHa: number | null;
  tillageDepth?: number | null;
}

const blankPractice: AppliedPractice = {
  id: undefined,
  practiceId: "",
  implementedOnHa: null,
  tillageDepth: null,
};

export const CropTillageAndSeedingForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropTillageAndSeedingFormProps) => {
  const { t } = useTranslation(["footprinting", "practices"]);
  const formRef = useRef<FormInstance>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  // Form elements
  const [tillageActive, setTillageActive] = useState<boolean | null>(null);
  const [seedingActive, setSeedingActive] = useState<boolean | null>(null);
  const [appliedTillages, setAppliedTillages] = useState<AppliedPractice[]>([]);
  const [appliedSeedings, setAppliedSeedings] = useState<AppliedPractice[]>([]);

  // GET Queries
  const seedingPractices = useGetPracticeSeedings({ enabled: true });
  const tillagePractices = useGetPracticeTillages({ enabled: true });
  const seedingsQuery = useGetFootprintingCropSeedingsByPrimaryDataId(primaryDataId);
  const tillagesQuery = useGetFootprintingCropTillagesByPrimaryDataId(primaryDataId);

  // Mutations
  const { mutate: mutateSeedings } = useMutateFootprintingCropSeedings(
    farmId,
    cropId,
    year,
    appliedSeedings
      .filter((t) => !!t.practiceId)
      .map((s) => ({
        id: s.id,
        footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
        practicesSeedingId: s.practiceId,
        implementedOnHa: s.implementedOnHa ?? null,
      })),
    primaryDataId ?? ""
  );

  const { mutate: mutateTillages } = useMutateFootprintingCropTillages(
    farmId,
    cropId,
    year,
    appliedTillages
      .filter((t) => !!t.practiceId)
      .map((t) => ({
        id: t.id,
        footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
        practicesTillageId: t.practiceId,
        implementedOnHa: t.implementedOnHa ?? null,
        tillageDepth: t.tillageDepth ?? null,
      })),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      tillageActive,
      seedingActive,
    },
    primaryDataId
  );

  const isLoading = [cropPrimaryDataQuery, tillagePractices, seedingPractices, seedingsQuery, tillagesQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateTillages();
        mutateSeedings();
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateSeedings, mutateTillages, readOnly]
  );

  useEffect(() => {
    formRef.current?.validateFields();
  }, [appliedSeedings, appliedTillages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (seedingsQuery.data && tillagesQuery.data) {
      setAppliedSeedings(
        seedingsQuery.data.length
          ? seedingsQuery.data.map((s) => ({
              id: s.id,
              practiceId: s.practicesSeedingId,
              implementedOnHa: s.implementedOnHa,
            }))
          : [{ ...blankPractice }]
      );

      setAppliedTillages(
        tillagesQuery.data.length
          ? tillagesQuery.data.map((t) => ({
              id: t.id,
              practiceId: t.practicesTillageId,
              implementedOnHa: t.implementedOnHa,
              tillageDepth: t.tillageDepth,
            }))
          : [{ ...blankPractice }]
      );
    }
  }, [seedingsQuery.data, tillagesQuery.data]);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setTillageActive(cropPrimaryDataQuery.data.tillageActive ?? null);
      setSeedingActive(cropPrimaryDataQuery.data.seedingActive ?? null);

      formRef.current?.setFieldsValue({
        tillageActive: cropPrimaryDataQuery.data.tillageActive ?? null,
        seedingActive: cropPrimaryDataQuery.data.seedingActive ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["tillageActive", "seedingActive"] as const).some((field) => cropPrimaryDataQuery.data?.[field] !== null);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  // This ensures that all form fields rerender when the non-last item is removed
  // (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedTillages.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedTillages.length]);

  // This ensures that all form fields rerender when the non-last item is removed
  // (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedSeedings.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedSeedings.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const tillagesSelectGroupName = "footprinting-crop-tillages-select";
  const seedingsSelectGroupName = "footprinting-crop-seedings-select";

  const disabledSeedings: Record<string, boolean> = appliedSeedings.reduce((acc, t) => ({ ...acc, [t.practiceId]: true }), {} as Record<string, boolean>);

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropTillageSeedingForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropTillageSeedingForm.info")}</p>
        </Flex>
      </Card>
      <Form dataTestId="footprinting-crop-tillage-seeding-form" ref={formRef}>
        <Card className="padding-100">
          <h3 className="padding-100">{t("cropTillageSeedingForm.tillage.subtitle")}</h3>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span style={{ maxWidth: "60%" }}>
              {t("cropTillageSeedingForm.tillage.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropTillageSeedingForm.tooltips.questionTillage")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              name="tillageActive"
              dataTestId="footprinting-crop-tillage-active-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setTillageActive(target.value);
              }}
              value={tillageActive}
              defaultValue={tillageActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {tillageActive &&
            appliedTillages.map((tillage, index) => {
              const groupWithIndex = `${tillagesSelectGroupName}-${index}`;
              const updateItem = (item: AppliedPractice) => {
                const next = [...appliedTillages];
                next[index] = { ...item };
                setAppliedTillages(next);
              };

              return (
                <Form.Item key={index} className="padding-100">
                  <Flex className="margin-bottom-100" justify="space-between" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("cropTillageSeedingForm.tillage.count") + (index + 1)}</h5>
                    <IconButton
                      type="tertiary"
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedTillages([...appliedTillages].filter((_, i) => i !== index))}
                      disabled={readOnly}
                    />
                  </Flex>
                  <Row>
                    <Col xs={28} sm={14}>
                      {/* @ts-expect-error need to convert to tsx */}
                      <Select
                        disabled={readOnly}
                        name={groupWithIndex}
                        dataTestId={groupWithIndex}
                        label={t("cropTillageSeedingForm.tillage.type")}
                        tooltip={t("cropTillageSeedingForm.tooltips.tillageType")}
                        optionFilterProp="children"
                        showSearch
                        rules={[{ message: false }]}
                        initialValue={tillage.practiceId}
                        value={tillage.practiceId}
                        onChange={(v) => updateItem({ ...tillage, practiceId: v })}
                        dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      >
                        {(tillagePractices.data ?? [])
                          .filter((practice) => practice.id !== ZERO_UUID)
                          .map((tillage) => (
                            <Option
                              disabled={readOnly}
                              key={`${groupWithIndex}-${tillage.id}`}
                              data-testid={`${groupWithIndex}-${tillage.id}`}
                              label={t(`soil.soilTypeOptions.${tillage}`)}
                              value={tillage.id}
                            >
                              {t(`${PRACTICE_SLUG.TILLAGES}.${tillage.translationSlug}`, { ns: "practices" })}
                            </Option>
                          ))}
                      </Select>
                    </Col>
                    <Col xs={20} sm={10} className="padding-0 padding-left-50">
                      <InputNumber
                        disabled={readOnly}
                        name={`${groupWithIndex}-coverage`}
                        dataTestId={`${groupWithIndex}-coverage`}
                        label={t("cropTillageSeedingForm.question.implementedOn")}
                        tooltip={t("cropTillageSeedingForm.tooltips.implementTillageOn")}
                        suffix="ha"
                        value={tillage.implementedOnHa}
                        initialValue={tillage.implementedOnHa}
                        onChange={(v) => updateItem({ ...tillage, implementedOnHa: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, message: t("cropTillageSeedingForm.error.validNumber") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropTillageSeedingForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                    <Col xs={48} sm={24} className="padding-0 padding-left-50 padding-top-50">
                      <InputNumber
                        disabled={readOnly}
                        name={`${groupWithIndex}-tillageDepth`}
                        dataTestId={`${groupWithIndex}-tillageDepth`}
                        label={t("cropTillageSeedingForm.tillage.depth")}
                        tooltip={t("cropTillageSeedingForm.tooltips.tillageDepth")}
                        suffix="cms"
                        value={tillage.tillageDepth}
                        initialValue={tillage.tillageDepth}
                        onChange={(v) => updateItem({ ...tillage, tillageDepth: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, type: "number", message: t("cropTillageSeedingForm.error.validNumber") },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropTillageSeedingForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Form.Item>
              );
            })}
          {tillageActive && (
            <Button
              type="text"
              onClick={() => {
                setAppliedTillages([...appliedTillages, { ...blankPractice }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropTillageSeedingForm.tillage.add")}
            </Button>
          )}
        </Card>

        <Card className="padding-100 margin-top-100">
          <h3 className="padding-100">{t("cropTillageSeedingForm.seeding.subtitle")}</h3>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span>
              {t("cropTillageSeedingForm.seeding.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropTillageSeedingForm.tooltips.questionSeeding")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              disabled={readOnly}
              name="seedingActive"
              dataTestId="footprinting-crop-seeding-active-radio"
              required
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setSeedingActive(target.value);
              }}
              value={seedingActive}
              defaultValue={seedingActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {seedingActive &&
            appliedSeedings.map((seeding, index) => {
              const groupWithIndex = `${seedingsSelectGroupName}-${index}`;
              const updateItem = (item: AppliedPractice) => {
                const next = [...appliedSeedings];
                next[index] = { ...item };
                setAppliedSeedings(next);
              };

              return (
                <Form.Item key={index} className="padding-100">
                  <Flex className="margin-bottom-100" justify="space-between" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("cropTillageSeedingForm.seeding.count") + (index + 1)}</h5>
                    <IconButton
                      disabled={readOnly}
                      type="tertiary"
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedSeedings([...appliedSeedings].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  <Row>
                    <Col xs={28} sm={14}>
                      {/* @ts-expect-error need to convert to tsx */}
                      <Select
                        name={groupWithIndex}
                        disabled={readOnly}
                        dataTestId={groupWithIndex}
                        label={t("cropTillageSeedingForm.seeding.type")}
                        tooltip={t("cropTillageSeedingForm.tooltips.seedingType")}
                        optionFilterProp="children"
                        showSearch
                        rules={[{ required: true, message: false }]}
                        initialValue={seeding.practiceId}
                        value={seeding.practiceId}
                        onChange={(v) => updateItem({ ...seeding, practiceId: v })}
                        dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      >
                        {(seedingPractices.data ?? [])
                          .filter((practice) => practice.id !== ZERO_UUID)
                          .map((seeding) => (
                            <Option
                              disabled={readOnly || disabledSeedings[seeding.id]}
                              key={`${groupWithIndex}-${seeding.id}`}
                              data-testid={`${groupWithIndex}-${seeding.id}`}
                              value={seeding.id}
                            >
                              {t(`${PRACTICE_SLUG.SEEDINGS}.${seeding.translationSlug}`, { ns: "practices" })}
                            </Option>
                          ))}
                      </Select>
                    </Col>

                    <Col xs={20} sm={10} className="padding-0 padding-left-50">
                      <InputNumber
                        name={`${groupWithIndex}-coverage`}
                        dataTestId={`${groupWithIndex}-coverage`}
                        disabled={readOnly}
                        label={t("cropTillageSeedingForm.question.implementedOn")}
                        tooltip={t("cropTillageSeedingForm.tooltips.implementSeedingOn")}
                        suffix="ha"
                        value={seeding.implementedOnHa}
                        initialValue={seeding.implementedOnHa}
                        onChange={(v) => updateItem({ ...seeding, implementedOnHa: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, type: "number", message: t("cropTillageSeedingForm.error.validNumber") },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropTillageSeedingForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Form.Item>
              );
            })}
          {seedingActive && (
            <Button
              disabled={readOnly}
              type="text"
              onClick={() => {
                setAppliedSeedings([...appliedSeedings, { ...blankPractice }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropTillageSeedingForm.seeding.add")}
            </Button>
          )}
        </Card>
      </Form>
    </div>
  );
};
